/**
 * UniversalGameLayout — Shared layout shell for all games during active gameplay.
 *
 * Provides:
 * - Collapsible IDE-style sidebars with tab strips
 * - Sticky game toolbar (turn, controls, status)
 * - Central board zone with game-over overlay
 * - How to Play slide-out drawer
 * - Dark mode, fullscreen, focus mode
 *
 * Usage:
 *   <UniversalGameLayout config={{ gameName: 'Chess', board: <ChessBoard />, ... }} />
 */

import React, { useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import './UniversalGameLayout.css';

// ============================================================================
// TypeScript Interfaces
// ============================================================================

/** Status bar / toolbar configuration */
export interface StatusBarConfig {
  turnIndicator?: ReactNode;
  connectionStatus?: 'connected' | 'reconnecting' | 'disconnected';
  roomCode?: string;
  gameStatus?: string;
  gameStatusType?: 'active' | 'check' | 'ended';
  aiThinking?: boolean;
  timer?: ReactNode;
}

/** Action button configuration */
export interface ActionConfig {
  id: string;
  label: string;
  icon?: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'accent';
}

/** Recent game entry */
export interface RecentGameEntry {
  id: string;
  result: 'win' | 'loss' | 'draw';
  opponent: string;
  date: number;
  moveCount: number;
}

/** Sidebar panel configuration */
export interface SidebarPanelConfig {
  id: string;
  title: string;
  icon: string;
  content: ReactNode;
  defaultFolded?: boolean;
  pinToBottom?: boolean; // For utility panels (Results, Appearance)
}

/** Game-over overlay configuration */
export interface GameOverConfig {
  show: boolean;
  result: 'win' | 'loss' | 'draw';
  title: string;
  subtitle?: string;
  icon?: string;
  onPlayAgain: () => void;
  onShare?: () => void;
  onExit: () => void;
}

/** Main layout configuration */
export interface UniversalGameLayoutConfig {
  // Required
  gameName: string;

  // Layout mode
  fullWidth?: boolean;
  isLoading?: boolean;

  // Content slots
  board?: ReactNode;
  leftPanels?: SidebarPanelConfig[];
  rightPanels?: SidebarPanelConfig[];

  // Toolbar
  statusBar?: StatusBarConfig;
  actions?: ActionConfig[];
  secondaryActions?: ReactNode[];

  // Game over
  gameOver?: GameOverConfig;

  // Recent games
  recentGames?: RecentGameEntry[];
  onReviewGame?: (gameId: string) => void;

  // Share
  onShare?: () => void;
  roomCode?: string;

  // How to Play
  howToPlayContent?: ReactNode;

  // Callbacks
  onThemeToggle?: () => void;
}

/** Component props */
export interface UniversalGameLayoutProps {
  config: UniversalGameLayoutConfig;
}

// ============================================================================
// Sub-Components
// ============================================================================

/** Foldable sidebar panel */
const SidebarPanel: React.FC<{
  panel: SidebarPanelConfig;
  folded: boolean;
  onToggleFold: () => void;
}> = React.memo(({ panel, folded, onToggleFold }) => (
  <div className={`ugl-panel ${folded ? 'ugl-panel--folded' : ''}`}>
    <div className="ugl-panel__header" onClick={onToggleFold}>
      <span>{panel.icon} {panel.title}</span>
      <span className="ugl-panel__chevron">{folded ? '▶' : '▼'}</span>
    </div>
    {!folded && <div className="ugl-panel__body">{panel.content}</div>}
  </div>
));
SidebarPanel.displayName = 'SidebarPanel';

/** Game toolbar */
const GameToolbar: React.FC<{
  config: UniversalGameLayoutConfig;
  onFocusMode: () => void;
  onFullscreen: () => void;
  onHowToPlay: () => void;
  onHistoryToggle?: () => void;
  hidden: boolean;
}> = React.memo(({ config, onFocusMode, onFullscreen, onHowToPlay, onHistoryToggle, hidden }) => {
  const { statusBar, actions, secondaryActions } = config;

  return (
    <div className={`ugl-toolbar ${hidden ? 'ugl-toolbar--hidden' : ''}`} role="toolbar" aria-label="Game controls">
      {/* Left: Status */}
      <div className="ugl-toolbar__section">
        {statusBar?.turnIndicator && (
          <div className="ugl-toolbar__turn" aria-live="polite" aria-atomic="true">
            {statusBar.turnIndicator}
          </div>
        )}
        {statusBar?.roomCode && (
          <span className="ugl-toolbar__chip ugl-toolbar__chip--room">{statusBar.roomCode}</span>
        )}
        {statusBar?.connectionStatus && (
          <span className="ugl-toolbar__chip">
            <span className={`ugl-toolbar__dot ugl-toolbar__dot--${statusBar.connectionStatus}`} />
            {statusBar.connectionStatus === 'connected' ? 'Online' : statusBar.connectionStatus}
          </span>
        )}
        {statusBar?.aiThinking && (
          <span className="ugl-toolbar__chip"><span className="ugl-toolbar__spinner" /> Thinking...</span>
        )}
        {statusBar?.gameStatus && (
          <span className={`ugl-toolbar__chip ugl-toolbar__chip--status ugl-toolbar__chip--status-${statusBar.gameStatusType || 'active'}`}>
            {statusBar.gameStatus}
          </span>
        )}
      </div>

      <div className="ugl-toolbar__spacer" />

      {/* Center: Actions */}
      <div className="ugl-toolbar__section">
        {actions?.map(action => (
          <button
            key={action.id}
            className={`ugl-toolbar__btn ugl-toolbar__btn--${action.variant || 'secondary'}`}
            onClick={action.onClick}
            disabled={action.disabled || config.gameOver?.show}
            title={action.label}
          >
            {action.icon && <span>{action.icon}</span>}
            <span>{action.label}</span>
          </button>
        ))}
        {config.roomCode && config.onShare && (
          <button
            className="ugl-toolbar__btn ugl-toolbar__btn--secondary"
            onClick={config.onShare}
            disabled={config.gameOver?.show}
            title="Share"
          >
            <span>📤</span>
            <span>Share</span>
          </button>
        )}
      </div>

      <div className="ugl-toolbar__divider" />

      {/* Right: Secondary + utilities */}
      <div className="ugl-toolbar__section">
        {secondaryActions?.map((node, i) => (
          <React.Fragment key={i}>{node}</React.Fragment>
        ))}
        {statusBar?.timer}
        {onHistoryToggle && (
          <button className="ugl-toolbar__btn ugl-toolbar__btn--secondary ugl-toolbar__btn--mobile-only" onClick={onHistoryToggle} title="History">
            <span>📋</span>
            <span>History</span>
          </button>
        )}
        <button className="ugl-toolbar__btn ugl-toolbar__btn--secondary" onClick={onHowToPlay} title="How to Play">❓</button>
        <button className="ugl-toolbar__btn ugl-toolbar__btn--secondary" onClick={onFocusMode} title="Focus Mode">👁️</button>
        <button className="ugl-toolbar__btn ugl-toolbar__btn--secondary" onClick={onFullscreen} title="Fullscreen">⛶</button>
      </div>
    </div>
  );
});
GameToolbar.displayName = 'GameToolbar';

/** Game-over overlay */
const GameOverOverlay: React.FC<{ config: GameOverConfig }> = React.memo(({ config }) => {
  if (!config.show) return null;
  return (
    <div className="ugl-overlay" role="dialog" aria-label="Game result">
      <div className="ugl-overlay__content">
        {config.icon && <div className="ugl-overlay__icon">{config.icon}</div>}
        <div className="ugl-overlay__title">{config.title}</div>
        {config.subtitle && <div className="ugl-overlay__subtitle">{config.subtitle}</div>}
        <div className="ugl-overlay__actions">
          <button className="ugl-overlay__btn ugl-overlay__btn--primary" onClick={config.onPlayAgain}>🔄 Play Again</button>
          {config.onShare && <button className="ugl-overlay__btn ugl-overlay__btn--ghost" onClick={config.onShare}>📤 Share</button>}
          <button className="ugl-overlay__btn ugl-overlay__btn--ghost" onClick={config.onExit}>🏠 Exit</button>
        </div>
      </div>
    </div>
  );
});
GameOverOverlay.displayName = 'GameOverOverlay';

/** Board error fallback */
const BoardErrorFallback: React.FC = () => (
  <div className="ugl__board-error">
    <div className="ugl__board-error-icon">⚠️</div>
    <div className="ugl__board-error-text">Something went wrong</div>
    <div className="ugl__board-error-hint">The game board encountered an error. Your sidebars and controls remain functional.</div>
  </div>
);

/** Recent Games panel */
const RecentGamesPanel: React.FC<{
  games: RecentGameEntry[];
  onReview: (id: string) => void;
}> = React.memo(({ games, onReview }) => {
  const [collapsed, setCollapsed] = useState(true);

  const resultIcon = (result: 'win' | 'loss' | 'draw') => {
    switch (result) {
      case 'win': return '🏆';
      case 'loss': return '😞';
      case 'draw': return '🤝';
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const displayGames = games.slice(0, 5);

  return (
    <div className={`ugl-panel ugl-recent-games ${collapsed ? 'ugl-panel--folded' : ''}`}>
      <div className="ugl-panel__header" onClick={() => setCollapsed(prev => !prev)}>
        <span>📋 Recent Games</span>
        <span className="ugl-panel__chevron">{collapsed ? '▶' : '▼'}</span>
      </div>
      {!collapsed && (
        <div className="ugl-panel__body ugl-recent-games__body">
          {displayGames.length === 0 ? (
            <div className="ugl-recent-games__empty">No recent games</div>
          ) : (
            <ul className="ugl-recent-games__list">
              {displayGames.map(game => (
                <li
                  key={game.id}
                  className="ugl-recent-games__entry"
                  onClick={() => onReview(game.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onReview(game.id); } }}
                >
                  <span className="ugl-recent-games__icon">{resultIcon(game.result)}</span>
                  <span className="ugl-recent-games__details">
                    <span className="ugl-recent-games__opponent">{game.opponent}</span>
                    <span className="ugl-recent-games__meta">{formatDate(game.date)} · {game.moveCount} moves</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
});
RecentGamesPanel.displayName = 'RecentGamesPanel';

/** How to Play drawer */
const HowToPlayDrawer: React.FC<{ open: boolean; onClose: () => void; content?: ReactNode; gameName: string }> = React.memo(({ open, onClose, content, gameName }) => (
  <>
    <div className={`ugl-drawer-bg ${open ? 'ugl-drawer-bg--open' : ''}`} onClick={onClose} />
    <div className={`ugl-drawer ${open ? 'ugl-drawer--open' : ''}`}>
      <div className="ugl-drawer__header">
        <span className="ugl-drawer__title">📖 How to Play {gameName}</span>
        <button className="ugl-drawer__close" onClick={onClose}>✕</button>
      </div>
      <div className="ugl-drawer__body">{content}</div>
    </div>
  </>
));
HowToPlayDrawer.displayName = 'HowToPlayDrawer';

// ============================================================================
// Main Component
// ============================================================================

export const UniversalGameLayout: React.FC<UniversalGameLayoutProps> = ({ config }) => {
  // State
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const [foldedPanels, setFoldedPanels] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    config.leftPanels?.forEach(p => { if (p.defaultFolded) initial[p.id] = true; });
    config.rightPanels?.forEach(p => { if (p.defaultFolded) initial[p.id] = true; });
    return initial;
  });

  // Fullscreen listener
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Handlers
  const toggleFold = useCallback((panelId: string) => {
    setFoldedPanels(prev => ({ ...prev, [panelId]: !prev[panelId] }));
  }, []);

  const toggleLeftSidebar = useCallback(() => setLeftCollapsed(prev => !prev), []);
  const toggleRightSidebar = useCallback(() => setRightCollapsed(prev => !prev), []);

  const handleFocusMode = useCallback(() => {
    setFocusMode(prev => {
      if (!prev) { setLeftCollapsed(true); setRightCollapsed(true); }
      return !prev;
    });
  }, []);

  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  const handleMobileHistoryToggle = useCallback(() => {
    setMobileHistoryOpen(prev => !prev);
  }, []);

  // Whether recent games are available
  const hasRecentGames = (config.recentGames?.length ?? 0) > 0;

  // Derived classes
  const rootClass = useMemo(() => {
    const classes = ['ugl'];
    if (config.fullWidth) classes.push('ugl--full-width');
    if (isFullscreen) classes.push('ugl--fullscreen');
    if (leftCollapsed) classes.push('ugl--left-collapsed');
    if (rightCollapsed) classes.push('ugl--right-collapsed');
    return classes.join(' ');
  }, [config.fullWidth, isFullscreen, leftCollapsed, rightCollapsed]);

  // Split panels into top and bottom (pinned)
  const leftTopPanels = config.leftPanels?.filter(p => !p.pinToBottom) || [];
  const leftBottomPanels = config.leftPanels?.filter(p => p.pinToBottom) || [];
  const rightTopPanels = config.rightPanels?.filter(p => !p.pinToBottom) || [];

  // Validation (after hooks to comply with rules-of-hooks)
  if (!config.gameName) {
    if (process.env.NODE_ENV === 'development') {
      console.error('UniversalGameLayout: gameName is required in config');
    }
    return null;
  }

  return (
    <>
      {/* Toolbar */}
      <GameToolbar
        config={config}
        onFocusMode={handleFocusMode}
        onFullscreen={handleFullscreen}
        onHowToPlay={() => setDrawerOpen(true)}
        onHistoryToggle={hasRecentGames ? handleMobileHistoryToggle : undefined}
        hidden={focusMode}
      />

      {/* Main Layout */}
      <div className={rootClass} role="main" aria-label={`${config.gameName} game`}>

        {/* Left Sidebar */}
        {!config.fullWidth && (
          <div className={`ugl__sidebar ugl__sidebar--left ${leftCollapsed ? 'ugl__sidebar--collapsed' : ''}`}>
            <div className="ugl__sidebar-tabs">
              {leftTopPanels.map(p => (
                <button
                  key={p.id}
                  className="ugl__sidebar-tab"
                  title={p.title}
                  onClick={() => { if (leftCollapsed) setLeftCollapsed(false); }}
                >
                  {p.icon}
                </button>
              ))}
              {leftBottomPanels.length > 0 && <div className="ugl__sidebar-tabs-spacer" />}
              {leftBottomPanels.map(p => (
                <button
                  key={p.id}
                  className="ugl__sidebar-tab"
                  title={p.title}
                  onClick={() => { toggleFold(p.id); if (leftCollapsed) setLeftCollapsed(false); }}
                >
                  {p.icon}
                </button>
              ))}
              {hasRecentGames && (
                <button
                  className="ugl__sidebar-tab"
                  title="Recent Games"
                  onClick={() => { if (leftCollapsed) setLeftCollapsed(false); }}
                >
                  📋
                </button>
              )}
              <button
                className="ugl__sidebar-tab ugl__sidebar-arrow"
                onClick={toggleLeftSidebar}
                title="Toggle sidebar"
              >
                {leftCollapsed ? '▶' : '◀'}
              </button>
            </div>
            {!leftCollapsed && (
              <div className="ugl__sidebar-content" role="complementary" aria-label="Game information">
                {[...leftTopPanels, ...leftBottomPanels].map(panel => (
                  <SidebarPanel
                    key={panel.id}
                    panel={panel}
                    folded={!!foldedPanels[panel.id]}
                    onToggleFold={() => toggleFold(panel.id)}
                  />
                ))}
                {hasRecentGames && config.onReviewGame && (
                  <RecentGamesPanel
                    games={config.recentGames!}
                    onReview={config.onReviewGame}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Board Zone */}
        <div className="ugl__board" role="region" aria-label={`${config.gameName} board`}>
          <ErrorBoundary fallback={<BoardErrorFallback />} componentName={`${config.gameName} Board`}>
            {config.isLoading ? (
              <div className="ugl__loading">
                <div className="ugl__loading-spinner" />
                <div className="ugl__loading-text">Loading {config.gameName}...</div>
              </div>
            ) : (
              config.board
            )}
          </ErrorBoundary>
          {config.gameOver && <GameOverOverlay config={config.gameOver} />}
        </div>

        {/* Right Sidebar */}
        {!config.fullWidth && (
          <div className={`ugl__sidebar ugl__sidebar--right ${rightCollapsed ? 'ugl__sidebar--collapsed' : ''}`}>
            <div className="ugl__sidebar-tabs">
              {rightTopPanels.map(p => (
                <button
                  key={p.id}
                  className="ugl__sidebar-tab"
                  title={p.title}
                  onClick={() => { if (rightCollapsed) setRightCollapsed(false); }}
                >
                  {p.icon}
                </button>
              ))}
              <button
                className="ugl__sidebar-tab ugl__sidebar-arrow"
                onClick={toggleRightSidebar}
                title="Toggle sidebar"
                style={{ marginTop: 'auto' }}
              >
                {rightCollapsed ? '◀' : '▶'}
              </button>
            </div>
            {!rightCollapsed && (
              <div className="ugl__sidebar-content" role="complementary" aria-label="Game tools">
                {rightTopPanels.map(panel => (
                  <SidebarPanel
                    key={panel.id}
                    panel={panel}
                    folded={!!foldedPanels[panel.id]}
                    onToggleFold={() => toggleFold(panel.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* How to Play Drawer */}
      <HowToPlayDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        content={config.howToPlayContent}
        gameName={config.gameName}
      />

      {/* Mobile Recent Games Bottom Sheet */}
      {hasRecentGames && config.onReviewGame && (
        <>
          <div
            className={`ugl-bottom-sheet-bg ${mobileHistoryOpen ? 'ugl-bottom-sheet-bg--open' : ''}`}
            onClick={() => setMobileHistoryOpen(false)}
          />
          <div className={`ugl-bottom-sheet ${mobileHistoryOpen ? 'ugl-bottom-sheet--open' : ''}`}>
            <div className="ugl-bottom-sheet__header">
              <span className="ugl-bottom-sheet__title">📋 Recent Games</span>
              <button className="ugl-bottom-sheet__close" onClick={() => setMobileHistoryOpen(false)}>✕</button>
            </div>
            <div className="ugl-bottom-sheet__body">
              <ul className="ugl-recent-games__list">
                {config.recentGames!.slice(0, 5).map(game => (
                  <li
                    key={game.id}
                    className="ugl-recent-games__entry"
                    onClick={() => { config.onReviewGame!(game.id); setMobileHistoryOpen(false); }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); config.onReviewGame!(game.id); setMobileHistoryOpen(false); } }}
                  >
                    <span className="ugl-recent-games__icon">
                      {game.result === 'win' ? '🏆' : game.result === 'loss' ? '😞' : '🤝'}
                    </span>
                    <span className="ugl-recent-games__details">
                      <span className="ugl-recent-games__opponent">{game.opponent}</span>
                      <span className="ugl-recent-games__meta">
                        {(() => {
                          const diffDays = Math.floor((Date.now() - game.date) / (1000 * 60 * 60 * 24));
                          if (diffDays === 0) return 'Today';
                          if (diffDays === 1) return 'Yesterday';
                          if (diffDays < 7) return `${diffDays}d ago`;
                          return new Date(game.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                        })()} · {game.moveCount} moves
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default UniversalGameLayout;
